/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { LocalStorageService } from '@hcworkspace/sysque/toolbelt/utils';
import { GlobalConstants } from '../constants/global-constants';
import { PanelCircuitDataReturnCodes } from '../models/panel-circuit-data-return-codes';
import { FamilyInformationItem } from '../models/family-information-model';
import { RotateElementReturnCodes } from '../../../../../shared/ui/src/lib/enums/rotate-element-return-codes';
import { ShiftSegmentReturnCodes } from '../../../../../shared/ui/src/lib/enums/shift-segment-return-codes';
import { WorksharingService } from 'libs/sysque/toolbelt/utils/src/lib/sysque-worksharing/sysque-worksharing.service';
import { ProcessingService } from 'libs/sysque/toolbelt/utils/src/lib/sysque-processing/sysque-processing.service';
import { DockableMessagesService } from '../../../../../toolbelt/utils/src/lib/sysque-dockable-messages/sysque-dockable-messages.service';
import { AcrylicSurfaceService } from '../acrylic-surface/acrylic-surface.service';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { SysQueGaugeReturnCodes } from '../models/sysque-gauge-return-codes';
import { ConvertOptionReturnCodes } from '../models/place-coupling-return-codes';
import { RotateElementDirection } from '../models/rotate-element-direction';
import { SysqueElectricalState } from '../models/sysque-electrical-state';
import { DeviceEquipmentDetailsReturnCodes } from '../models/device-equipment-details-return-codes';

export class DockableReceiveController {
  constructor(
    private localStorageService: LocalStorageService,
    private worksharingService: WorksharingService,
    private processingService: ProcessingService,
    private dockableMessagesService: DockableMessagesService,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  public GetSystemsList(systemsList: SystemInfo[] | any): void {
    const systemList: SystemInfo[] = (systemsList as SystemInfo[]).map((item) => {
      const transformedItem = {};
      Object.keys(item).forEach((key) => {
        const newKey = key.charAt(0).toLowerCase() + key.slice(1);
        transformedItem[newKey] = item[key];
      });
      return {
        id: transformedItem['id'],
        name: transformedItem['name'],
        unitType: transformedItem['unitType'],
        inVisibility: transformedItem['inVisibility'],
        systemShape: transformedItem['systemShape'],
        isHangerEnabled: transformedItem['isHangerEnabled'],
        isClearance: transformedItem['isClearance'],
      } as SystemInfo;
    });
    this.processingService.setSystemsInfo(systemList);
  }

  public UpdateSelectionChangesStatus(response: boolean): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.ELEMENT_SELECTION_STATUS, response);
  }

  public DownloadGenericFittingsStart(): void{
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.DOWNLOAD_GENERIC_FAMILIES, 'true');
  }

  public DownloadGenericFittingsEnd(missingFamilies: boolean): void{
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.CHECK_MISSING_FAMILIES, missingFamilies);
  }

  public NeedToSetUpHanger(): void{
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SET_UP_HANGER, 'true');
  }

  public ApplyPanelDataStatus(response: PanelCircuitDataReturnCodes, fittingCount: number): void {
    this.localStorageService.updateDispatchLocalStorage(
      GlobalConstants.DOCKABLE_PANEL_DATA_FITTING_COUNT,
      fittingCount
    );
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.DOCKABLE_PANEL_DATA_RESPONSE, response);
  }

  public CableTrayFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public ConduitFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public JunctionBoxFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public FieldFabFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public HandAndMachineBendFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public KickHandAndMachineBendFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public UserCustomContentFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public HangerSpecificationFamilyInformationResult(response: FamilyInformationItem): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE, response);
  }

  public SendRefreshDataRequest(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.REFRESH_ADD_FITTING_DIALOG_DATA, undefined);
  }

  public ShowNonStandardResult(response: boolean): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SHOW_NON_STANDARD_RESPONSE, response);
  }

  public RotateElementResult(response: RotateElementReturnCodes): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.ROTATE_ELEMENT_RESPONSE, response);
  }

  public ShiftSegmentResult(response: ShiftSegmentReturnCodes): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SHIFT_SEGMENT_RESPONSE, response);
  }

  public WorksharingProgressStartMessage(): void {
    this.worksharingService.setWorksharingInProgress(true);
  }

  public WorksharingProgressEndMessage(): void {
    this.worksharingService.setWorksharingInProgress(false);
  }

  public WorksharingSyncMessage(): void {
    this.worksharingService.setWorksharingSyncNecessary(true);
  }

  public DuplicationDefaultTypeNotFound(): void {
    this.worksharingService.setDefaultTypeNotFoundForDuplicate(true);
  }

  public InitializeProcessing(): void {
    this.processingService.setInitializeProcessing(true);
  }

  public StopProcessing(): void {
    this.processingService.setInitializeProcessing(false);
  }

  public ProcessingWorksharingNoAccess(): void {
    this.processingService.setProcessingWorksharingNoAccess(true);
  }

  public ProcessingNoActiveDocument(): void {
    this.processingService.setProcessingNoActiveDocument(true);
  }

  public ProcessingCableTrayFittings(numberOfFittings: number): void {
    this.processingService.setProcessingCableTrayFittings(numberOfFittings);
  }

  public ProcessingCableTrays(numberOfCableTrays: number): void {
    this.processingService.setProcessingCableTrays(numberOfCableTrays);
  }

  public ProcessingConduitFittings(numberOfFittings: number): void {
    this.processingService.setProcessingConduitFittings(numberOfFittings);
  }

  public ProcessingConduits(numberOfCableTrays: number): void {
    this.processingService.setProcessingConduits(numberOfCableTrays);
  }

  public ProcessingFieldFabConduits(numberOfCableTrays: number): void {
    this.processingService.setProcessingFieldFabConduits(numberOfCableTrays);
  }

  public ProcessingExtendTrimError(): void {
    this.processingService.setProcessingExtendTrimError(true);
  }

  public ProcessingConduitRunsBendAngleError(): void {
    this.processingService.setProcessingConduitRunsBendAngleError(true);
  }

  public ProcessingConduitRunsAngleError(maxAngle: number, bendAngle: number): void {
    this.processingService.setProcessingConduitRunsAngleError(maxAngle, bendAngle);
  }

  public ProcessingFabConduitsConnectivity(totalCount: number): void {
    this.processingService.setProcessingFabConduitsConnectivity(totalCount);
  }

  public ProcessingTakeOffFittings(totalCount: number): void {
    this.processingService.setProcessingTakeOffFittings(totalCount);
  }
  
  public SelectionMismatchCableTray(): void {
    this.dockableMessagesService.setSelectionMismatchCodeChecking(true);
  }

  public SelectionMismatchConduits(): void {
    this.dockableMessagesService.setSelectionMismatchConduits(true);
  }

  public SpecificationMissing(): void {
    this.dockableMessagesService.setSpecificationMissing(true);
  }

  public SelectionMismatchEquipment(): void {
    this.dockableMessagesService.setSelectionMismatchEquipment(true);
  }

  public FamilyNotExists(): void {
    this.dockableMessagesService.setFamilyNotExists(true);
  }

  public InvalidSelection(): void {
    this.dockableMessagesService.setInvalidSelection(true);
  }

  public CodeCheckingExceedingMaximumAngle(isExceeded: boolean, maxAngle: number, bendAngle: number): void {
    this.dockableMessagesService.setCodeCheckingExceedingMaximumAngle(isExceeded, maxAngle, bendAngle);
  }

  public ProcessingApplySystemData(count: number): void {
    this.dockableMessagesService.setProcessingApplySystemData(count);
  }

  public UpdatingNominalSizeStatus(totalCount: number): void {
    this.dockableMessagesService.setUpdatingNominalSizeStatus(totalCount);
  }

  public UpdatingExtendedData(totalCount: number): void {
    this.dockableMessagesService.setUpdatingExtendedData(totalCount);
  }

  public WireFillSpecInformationNotAvailable(): void {
    this.dockableMessagesService.setWireFillSpecInformationNotAvailable(true);
  }

  public CommentsOrConduitIdMissing(): void {
    this.dockableMessagesService.setCommentsOrConduitIdMissing(true);
  }

  public ConduitFillExceeds40PercentCheckingOrNotError(isExceeded: boolean, isNecMaximumFill: boolean): void {
    this.dockableMessagesService.setConduitFillExceeds40PercentCheckingOrNotError(isExceeded,isNecMaximumFill);
  }

  public CloseAction(): void {
    this.acrylicSurfaceService.hide();
  }

  public CloseActionSetDockTrue(): void {
    this.acrylicSurfaceService.hide();
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.AddFittings, false);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SystemManageKey, false);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.DockableKey, true);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.UpdateSystemInfoOnDockableFromAddFittings, true);
  }

  public SysQueConnectResult(response: SysQueGaugeReturnCodes): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSQUE_CONNECT_RESULT, response);
  }

  public ApplyPanelDataValidationResult(response: PanelCircuitDataReturnCodes): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.PANEL_DATA_VALIDATION_RESPONSE, response);
  }

  public SendSystemNameForGoToSystem(response: string): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSTEM_NAME_GO_TO_SYSTEM, response);
  }

  public UnableToProcessFittingsError(): void {
    this.dockableMessagesService.setUnableToProcessFittingsError(true);
  }

  public UpdateProjectList(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.UPDATE_PROJECT_LIST, true);
  }

  public UpdatePanelList(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.UPDATE_PANEL_LIST, true);
  }

  public ConvertOptionResult(response: ConvertOptionReturnCodes): void{
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.CONVERT_SYSTEM_RESPONSE, response);
  }

  public DownloadFamilies(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.DOWNLOAD_MISSING_FAMILIES, 'true');
  }

  public UpdateGenericFamilies(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.UPDATE_FAMILIES, 'true');
  }

  public UpdateShowNonStandard(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.UPDATE_SHOW_NON_STADARD, 'true');
  }

  public RotateElement(direction: RotateElementDirection): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.ROTATE_ELEMENT, direction);
  }

  public ShowAcrylicSurface(): void {
    this.acrylicSurfaceService.show();
  }

  public NoSystemSelected(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.NO_SYSTEM_SELECTED, true);
  }

  public SystemNameDoesNotExist(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSTEM_NAME_DOES_NOT_EXIST, true);
  }

  public FeatureMayNotWorkWithSelection(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FEATURE_MAY_NOT_WORK_WITH_SELECTION, true);
  }

  public SelectConduitCableTrayFitting(): void {
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SELECT_CONDUIT_CABLE_TRAY_FITTING, true);
  }

  public ReleaseVersionAndIsLicenseValid(releaseVersion: string, isLicenseValid: boolean): void {
    let state: SysqueElectricalState = this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    state.releaseVersion = releaseVersion;
    state.isLicenseValid = isLicenseValid;    
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, state);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.DOCK_RELEASE_VERSION_LICENSE_STATUS, { releaseVersion, isLicenseValid });
  }
}
